/**
 * ClienteFormIdentificacao - Sub-component for ClienteFormModal
 * Renders: Razão Social, Fantasia, CNPJ, Fone, Ativo, Input saldos, Regime
 */
import React from 'react';
import { Phone } from 'lucide-react';
import { ClienteInput } from '../services/clienteService';

interface ClienteFormIdentificacaoProps {
  formData: ClienteInput;
  errors: Record<string, string>;
  loading: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
}

const ClienteFormIdentificacao: React.FC<ClienteFormIdentificacaoProps> = ({
  formData,
  errors,
  loading,
  onChange,
}) => (
  <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
    <h3 className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-2 uppercase tracking-wide">
      Identificação
    </h3>
    <div className="space-y-2">
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
          Razão Social *
        </label>
        <input
          type="text"
          name="razao_social"
          value={formData.razao_social}
          onChange={onChange}
          className={`w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
            errors.razao_social ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
          }`}
          placeholder="SupriSul Suprimento Médicos Ltda"
          disabled={loading}
        />
        {errors.razao_social && (
          <p className="mt-0.5 text-xs text-red-600">{errors.razao_social}</p>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
          Fantasia
        </label>
        <input
          type="text"
          name="nome_fantasia"
          value={formData.nome_fantasia}
          onChange={onChange}
          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="SupriSul"
          disabled={loading}
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
          CNPJ
        </label>
        <input
          type="text"
          name="cnpj"
          value={formData.cnpj}
          onChange={onChange}
          className={`w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
            errors.cnpj ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
          }`}
          placeholder="16.226.301/0001-91"
          disabled={loading}
          maxLength={18}
        />
        {errors.cnpj && (
          <p className="mt-0.5 text-xs text-red-600">{errors.cnpj}</p>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
          <Phone className="w-3 h-3 inline mr-1" />
          Fone
        </label>
        <input
          type="text"
          name="telefone"
          value={formData.telefone}
          onChange={onChange}
          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="(51) 99999-9999"
          disabled={loading}
          maxLength={15}
        />
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2 space-y-1.5">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            name="ativo"
            checked={formData.ativo}
            onChange={onChange}
            className="w-3 h-3 text-blue-600 rounded focus:ring-1 focus:ring-blue-500"
            disabled={loading}
          />
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
            Cliente Ativo
          </span>
        </label>

        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            name="input_saldos_sistema_via_lotes"
            checked={formData.input_saldos_sistema_via_lotes}
            onChange={onChange}
            className="w-3 h-3 text-blue-600 rounded focus:ring-1 focus:ring-blue-500"
            disabled={loading}
          />
          <span className="text-xs text-gray-700 dark:text-gray-300">Input saldos via lotes</span>
        </label>

        <div className="flex gap-4 pt-1">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              name="com_regime"
              checked={formData.com_regime || false}
              onChange={onChange}
              className="w-3 h-3 text-blue-600 rounded focus:ring-1 focus:ring-blue-500"
              disabled={loading}
            />
            <span className="text-xs text-gray-700 dark:text-gray-300">Com Regime</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              name="sem_regime"
              checked={formData.sem_regime || false}
              onChange={onChange}
              className="w-3 h-3 text-blue-600 rounded focus:ring-1 focus:ring-blue-500"
              disabled={loading}
            />
            <span className="text-xs text-gray-700 dark:text-gray-300">Sem Regime</span>
          </label>
        </div>
      </div>
    </div>
  </div>
);

export default ClienteFormIdentificacao;
